import { action, makeObservable, observable, when } from 'mobx';
import { Constraint, generateEmailFieldConstraints } from '../utils/validators';
import { FormField } from './formField';
import { RootStore } from './root';
import { ROUTES } from '../routes';
import {
  ACCOUNT_RECOVERY_FORGOT_PASSWORD_URL,
  AUTH_METHODS_BY_MAIL,
  NEW_ACCOUNT_RECOVERY_URL,
  IAUTH_METHODS_BY_MAIL,
  LOGIN_PAGE_CONTEXT,
  RECOVERY_FORWARDED_USER_TYPES,
  SIGNUP_PAGE_CONTEXT,
  canonicalUserType,
  getVerticalSupportUrl,
  WIX_DEFAULT_PARENT_ACCOUNT_ID,
  WIX_PARENT_ACCOUNT_HINT,
} from '../utils/constants';
import { HttpError } from '@wix/http-client';
import { AccountData, UserAccountsDataByEmailResponse } from '../types';
import {
  onSwitchContext,
  userClickedOnContinueWithEmail,
} from '@wix/bi-logger-hls2/v2';
import { loginPageClickOnLogInWithSso } from '@wix/bi-logger-identity-data/v2';
import { CAPTCHA_ACTIONS } from './captcha';
import { LoginEmailStepStore } from './loginEmailStep';
import { ISignupFlows, SIGNUP_FLOWS } from './signup';
import { LOGIN_FLOWS } from './login';
import { areAllUserAccountsBlocked, ERROR_CODES } from '../utils/errorHandler';
import { AUTH_TYPE } from '../components/BlockedAccount/authTypes';
import { EXPERIMENTS } from './constants';

export type AuthHandlers = {
  MULTI: (params: { email: string }) => void;
  SINGLE: (params: { email: string; ssoUrl: string }) => void;
  LOGIN_PASSWORD: (params?: {
    flow?: string;
    withEmailCode?: boolean;
  }) => Promise<void>;
  SOCIAL_ONLY: (params: { socialConnectionsProviders: string[] }) => void;
  SIGNUP_PASSWORD: (params: { flow?: ISignupFlows }) => void | Promise<void>;
};

/**
 * The backend sends a login code only to a verified email that belongs to exactly
 * one user under the Wix default parent account; anything else is refused.
 */
const isEmailCodeLoginEligible = (
  response?: UserAccountsDataByEmailResponse,
) => {
  const accounts = response?.accountsData ?? [];
  const parentAccountId = accounts[0]?.parentAccountId;
  return (
    response?.emailVerified === true &&
    accounts.length === 1 &&
    (!parentAccountId || parentAccountId === WIX_DEFAULT_PARENT_ACCOUNT_ID)
  );
};

/** The one account behind the email has no password to log in with. */
const isPasswordlessEmailCodeAccount = (
  response?: UserAccountsDataByEmailResponse,
) =>
  isEmailCodeLoginEligible(response) &&
  response?.accountsData?.[0]?.userAuthenticatorsData?.hasPassword === false;

export class EmailStepStore implements AuthStore {
  protected readonly rootStore: RootStore;
  private captchaAdded: boolean = false;
  public authHandlers: AuthHandlers;
  public emailField: FormField;
  public shouldRememberMe: boolean = true;
  public isLoading: boolean = false;
  public titleKey: string;
  public subtitleKey: string;
  public isEmailLoginMobileMode: boolean = false;
  public isFetchingByDefaultEmail: boolean = true;
  private EMAIL_CHECK_INTERACTION_NAME = 'email-check';
  public loginEmailStepStore: LoginEmailStepStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    const { i18n, displayStore } = rootStore;
    this.titleKey =
      displayStore.preset.landingPage?.title?.key ?? 'emailStep.title';
    this.subtitleKey =
      displayStore.preset.landingPage?.subtitle?.key ?? 'emailStep.subTitle';
    makeObservable(this, {
      getContext: action.bound,
      submit: action.bound,
      onClickContinueWithSso: action.bound,
      onClickForgotEmail: action.bound,
      onClickForgotPassword: action.bound,
      shouldRememberMe: observable,
      isLoading: observable,
      isFetchingByDefaultEmail: observable,
      isEmailLoginMobileMode: observable,
    });
    const emailRules: Constraint[] = generateEmailFieldConstraints(
      i18n.t.bind(i18n),
    );
    this.emailField = new FormField(
      this.rootStore.userDataStore.email,
      emailRules,
    );

    if (this.rootStore.socialAuthStore.isUnsupportedAgentForSocialAuth) {
      this.isEmailLoginMobileMode = true;
    }
    this.loginEmailStepStore = new LoginEmailStepStore(rootStore);
    this.handlePostLoaded();
  }

  clear(): void {
    this.emailField.clear();
    this.isLoading = false;
    this.isFetchingByDefaultEmail = true;
  }

  onInitFields(): void {
    const { email } = this.rootStore.userDataStore;
    email && this.emailField.setValue(email);
  }

  presetSubtitleKey?: string | undefined;

  private async handlePostLoaded() {
    await when(() => this.rootStore.isLoaded);
    this.setAuthHandlers();
    this.handleAutoSubmission();
  }

  public async attemptUsernamelessWebAuthnLogin() {
    await when(() => this.rootStore.isLoaded);

    try {
      const webAuthnResult =
        await this.rootStore.webauthnStore.authenticateWithUsernamelessWebAuthn();

      if (webAuthnResult.status !== 'success') {
        // Usernameless flow is optional - silent fallback on cancel or error
        return;
      }

      return this.rootStore.loginStore.login('', {
        publicKeyCredential: webAuthnResult.data.credential,
        webAuthnChallenge: webAuthnResult.data.challenge,
      });
    } catch (error) {}
  }

  protected setAuthHandlers() {
    const { ssoStore, socialAuthStore, signupStore, loginStore } =
      this.rootStore;
    const showLoginEmailStep = () =>
      this.loginEmailStepStore.showLoginEmailStep;
    const getLoginFlow = (flow?: string) => {
      if (flow === SIGNUP_FLOWS.DEFAULT) {
        return LOGIN_FLOWS.FROM_SIGNUP;
      }
      return flow;
    };
    this.authHandlers = {
      [AUTH_METHODS_BY_MAIL.MULTI]: ssoStore.navigateAccountSelector,
      [AUTH_METHODS_BY_MAIL.SINGLE]: ssoStore.redirectSsoUrl,
      [AUTH_METHODS_BY_MAIL.LOGIN_PASSWORD]: async (params?: {
        flow?: string;
        withEmailCode?: boolean;
      }) => {
        const loginFlow =
          getLoginFlow(params?.flow) ||
          (showLoginEmailStep()
            ? LOGIN_FLOWS.DEFAULT
            : LOGIN_FLOWS.FROM_SIGNUP);
        if (params?.withEmailCode) {
          return loginStore.onNavigateToLoginWithEmailCode(loginFlow);
        }
        return loginStore.onNavigateToLogin(loginFlow);
      },
      [AUTH_METHODS_BY_MAIL.SOCIAL_ONLY]: (params) =>
        socialAuthStore.setSocialProvidersAndNavigate({
          ...params,
          ...(!showLoginEmailStep() ? { showAlreadyExistsNote: true } : {}),
        }),
      [AUTH_METHODS_BY_MAIL.SIGNUP_PASSWORD]: (params?: {
        flow?: ISignupFlows;
      }) =>
        signupStore.onNavigateToSignup(
          params?.flow ||
            (showLoginEmailStep()
              ? SIGNUP_FLOWS.FROM_LOGIN
              : SIGNUP_FLOWS.DEFAULT),
        ),
    };
  }

  private async handleAutoSubmission() {
    const {
      navigationStore: { currentRoute, loginDialogContext },
      userDataStore,
    } = this.rootStore;
    const matchingDialogContext =
      !loginDialogContext ||
      [LOGIN_PAGE_CONTEXT, SIGNUP_PAGE_CONTEXT].some(
        (context) => context === loginDialogContext,
      );
    if (
      userDataStore.email &&
      currentRoute === ROUTES.EMAIL_STEP &&
      matchingDialogContext
    ) {
      await this.submit(true);
    }
    this.isFetchingByDefaultEmail = false;
  }

  /**
   * Records whether the looked-up email may log in with a code, and returns
   * it. Login offers the code only to an account without a password.
   */
  public recordEmailCodeEligibility(
    email: string,
    response?: UserAccountsDataByEmailResponse,
  ) {
    const eligible = isPasswordlessEmailCodeAccount(response);
    this.rootStore.loginStore.setCodeEligibleEmail(email, eligible);
    return eligible;
  }

  public getAuthMethodByAccountData(
    accounts: AccountData[],
    email: string,
  ): { method: IAUTH_METHODS_BY_MAIL; params?: { [key: string]: any } } {
    // Every lookup re-resolves the account, so drop the previous account's
    // social connections first - otherwise a path that returns before an
    // account is resolved (multiple accounts, no account) would leave the
    // Facebook gate reading a stale account.
    this.rootStore.socialAuthStore.setAccountSocialConnectionsProviders([]);
    // Should never happen since the server suppose to response with 404 in such a case
    if (!accounts && !this.rootStore.loginStore.shouldBlockEditorXSignup()) {
      return { method: AUTH_METHODS_BY_MAIL.SIGNUP_PASSWORD };
    }
    const getQueryParam = this.rootStore.navigationStore.getQueryParam;
    const parentHint = getQueryParam('parentHint');
    /** Multiple accounts **/
    if (accounts.length > 1 && !parentHint) {
      return { method: AUTH_METHODS_BY_MAIL.MULTI };
    }
    /** Single account **/
    let account = accounts[0];
    if (accounts.length > 1 && parentHint) {
      const filtered = accounts.filter(
        (a) =>
          (!a.parentAccountId && parentHint === WIX_PARENT_ACCOUNT_HINT) ||
          a.parentAccountId === parentHint,
      );
      if (filtered.length) {
        account = filtered[0];
      }
    }
    // Expose the resolved account's social connections so the following steps
    // can gate the Facebook SSO button (Symphony
    // `shouldSymphonyHideFacebookUnlessConnected` Conductor FT hides Facebook
    // unless this account actually has it).
    this.rootStore.socialAuthStore.setAccountSocialConnectionsProviders(
      account?.userAuthenticatorsData?.socialConnectionsProviders ?? [],
    );
    const ssoUrl = account?.accountSsoSettings?.ssoUrl;
    const isSsoMandatory =
      ssoUrl &&
      (account?.accountSsoSettings?.isSsoMandatory ||
        !account?.userAuthenticatorsData?.hasPassword);
    if (isSsoMandatory) {
      return {
        method: AUTH_METHODS_BY_MAIL.SINGLE,
        params: { ssoUrl },
      };
    }
    if (this.rootStore.socialAuthStore.shouldProceedToSocialAuthOnly(account)) {
      return {
        method: AUTH_METHODS_BY_MAIL.SOCIAL_ONLY,
        params: {
          socialConnectionsProviders:
            account?.userAuthenticatorsData?.socialConnectionsProviders,
          ssoUrl,
        },
      };
    }
    return { method: AUTH_METHODS_BY_MAIL.LOGIN_PASSWORD };
  }

  public async submit(isDefaultEmail: boolean = false) {
    !isDefaultEmail &&
      this.rootStore.biLogger.report(userClickedOnContinueWithEmail({}));

    if (!this.emailField.isValid || this.isLoading) {
      this.emailField.markFieldAsDirty();
      return;
    }
    this.isLoading = true;
    this.handleEmailSubmit(this.emailField.value)
      .catch((e) => {
        if (e.message === ERROR_CODES.ACCOUNT_NOT_FOUND_AND_SIGNUP_DISABLED) {
          this.emailField.addError(
            this.rootStore.i18n.t('account.not.found.error'),
          );
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  public async handleEmailSubmit(email: string, flow?: string) {
    this.rootStore.webauthnStore.abortWebAuthnAuthentication();

    this.rootStore.panorama.startTransaction(this.EMAIL_CHECK_INTERACTION_NAME);
    const recaptchaParams =
      await this.rootStore.captchaStore.handleRecaptchaExecution({
        captchaAdded: this.captchaAdded,
        action: CAPTCHA_ACTIONS.GET_USER_ACCOUNTS,
      });

    this.rootStore.userDataStore.setEmail(email);
    this.rootStore.socialAuthStore.setAccountSocialConnectionsProviders([]);
    const { apiStore } = this.rootStore;

    try {
      const response = await apiStore.getUserAccounts({
        email,
        ...recaptchaParams,
      });

      const authType = this.getAuthMethodByAccountData(
        response?.accountsData!,
        email,
      );
      const withEmailCode = this.recordEmailCodeEligibility(email, response);
      this.rootStore.panorama.finishTransaction(
        this.EMAIL_CHECK_INTERACTION_NAME,
      );

      const shouldNavigateToBlockedAccount = this.rootStore.experiments.enabled(
        'specs.login.ShouldNavigateToBlockedAccount',
      );
      if (
        shouldNavigateToBlockedAccount &&
        response.accountsData &&
        areAllUserAccountsBlocked(response.accountsData)
      ) {
        return this.rootStore.navigationStore.navigateToBlockedAccount({
          refferal_info: AUTH_TYPE.EMAIL_AND_PASSWORD,
        });
      }

      if (
        authType.method === AUTH_METHODS_BY_MAIL.LOGIN_PASSWORD &&
        this.rootStore.experiments.enabled(EXPERIMENTS.ENABLE_PASSKEY_LOGIN) &&
        !this.rootStore.webauthnStore.isPasskeyFlowDisabled
      ) {
        const passkeyResult =
          await this.rootStore.webauthnStore.loginWithPasskey(email);
        if (passkeyResult.status === 'success') {
          return;
        }
        // On cancellation or error, fall back to password login
        // (error toast is shown by loginWithPasskey for actual errors)
      }

      await this.authHandlers[authType.method]({
        email,
        flow,
        withEmailCode,
        ...(authType.params ?? {}),
      } as any);
    } catch (error: any) {
      this.isLoading = false;
      const serverErrorCode = (
        error as HttpError
      )?.response?.data.errorCode?.toString();
      const emailDoesNotExist = this.isUserEmailDoesNotExist(error);
      const isValidInteraction =
        emailDoesNotExist ||
        this.rootStore.captchaStore.isCaptchaServerError(serverErrorCode);
      if (isValidInteraction) {
        this.rootStore.panorama.finishTransaction(
          this.EMAIL_CHECK_INTERACTION_NAME,
        );
      }
      if (emailDoesNotExist) {
        if (this.rootStore.loginStore.shouldBlockEditorXSignup()) {
          throw new Error(ERROR_CODES.ACCOUNT_NOT_FOUND_AND_SIGNUP_DISABLED);
        } else {
          this.isLoading = true;
          try {
            await this.authHandlers[AUTH_METHODS_BY_MAIL.SIGNUP_PASSWORD]({
              flow: flow as ISignupFlows,
            });
          } finally {
            this.isLoading = false;
          }
        }
      }
      this.captchaAdded =
        this.rootStore.captchaStore.createOrResetCaptchaIfNeeded(
          serverErrorCode,
          this.captchaAdded,
        );
    }
  }

  isUserEmailDoesNotExist(error) {
    return (error as HttpError)?.response?.status?.toString() === '404';
  }

  getAccountRecoveryLink(mode: 'email' | 'password') {
    const accountRecoveryParams = new URLSearchParams({
      sessionId: this.rootStore.sessionId.get(),
      from: window.location.href,
    });

    // Recovery renders unbranded and mails an unbranded reset link unless it is
    // told which vertical the user came from — nothing else in the hand-off
    // carries it. See RECOVERY_FORWARDED_USER_TYPES for why this is a list.
    const userType = canonicalUserType(this.rootStore.userType);
    if (userType && RECOVERY_FORWARDED_USER_TYPES.includes(userType)) {
      accountRecoveryParams.set('userType', userType);
    }

    if (mode === 'password') {
      return `${ACCOUNT_RECOVERY_FORGOT_PASSWORD_URL}?${accountRecoveryParams.toString()}`;
    }
    if (mode === 'email') {
      // The questionnaire answers "which email did I sign up with?" by looking up
      // the Wix site the user owns. These verticals own none, so it cannot answer
      // it — sending them there only brands a dead end.
      const supportUrl = getVerticalSupportUrl(userType);
      if (supportUrl) {
        return supportUrl;
      }

      return `${NEW_ACCOUNT_RECOVERY_URL}?${accountRecoveryParams.toString()}`;
    }

    return `/account-recovery?${accountRecoveryParams.toString()}`;
  }

  public getSsoWithEmailLink() {
    return ROUTES.SSO_ENTER_EMAIL;
  }

  onClickForgotEmail() {
    this.reportRecoverySwitch('forgot-email');
  }

  /** The landing step offers this instead of forgot-email for some presets. */
  onClickForgotPassword() {
    this.reportRecoverySwitch('forgot-password');
  }

  private reportRecoverySwitch(context: 'forgot-email' | 'forgot-password') {
    this.rootStore.biLogger.report(
      onSwitchContext({
        context,
        referrer: this.rootStore.navigationStore.originContext,
      }),
    );
  }

  onClickContinueWithSso() {
    this.rootStore.biLogger.report(loginPageClickOnLogInWithSso({}));
    this.rootStore.navigationStore.navigate(this.getSsoWithEmailLink());
  }

  getContext() {
    return this.loginEmailStepStore.showLoginEmailStep
      ? LOGIN_PAGE_CONTEXT
      : SIGNUP_PAGE_CONTEXT;
  }
}
